import axios, { AxiosError, AxiosRequestConfig } from "axios";
import Vue from "vue";
import { ScopeSet, OIDC_DEFAULT_SCOPES } from "@azure/msal-common";
import { MsalPlugin } from "./msalPlugin";
import userInfoService from "@/util/userInfoService";
import {
    BEARER_PREFIX,
    computeBackoffDelay,
    hasValidAuthHeader,
    stripApiPrefix,
    syncGroupClaims,
    TOKEN_EXPIRY_BUFFER_MS,
} from "./authShared";

const offlineAccessIndex = OIDC_DEFAULT_SCOPES.indexOf("offline_access");
if (offlineAccessIndex !== -1) {
    OIDC_DEFAULT_SCOPES.splice(offlineAccessIndex, 1);
}

// Debug toggle for exercising silent token renewal locally. Disabled by default (0).
// When set > 0 it: (1) forces silent renewal on this fixed interval (ms) instead of the
// normal ~10-min-before-expiry schedule, (2) forces a real network refresh (forceRefresh)
// so the token expiry advances each tick, and (3) logs every renewal attempt to the console.
// Keep this 0 for committed code; set to e.g. 30 * 1000 only for local testing.
const DEBUG_RENEWAL_INTERVAL_MS = 0; // ms; 0 = disabled (normal expiry-based schedule)

export interface MicrosoftAuthOptions {
    serverUrl: string,
    msalConfig: any,
    authProviderType?: string
}

export default {
    isAuthenticating: false,
    authenticationPromise: null as Promise<void> | null,
    cachedToken: null as any,
    tokenExpiration: null as Date | null,
    renewalTimer: null as NodeJS.Timeout | null,
    // Token acquisition debouncing
    isFetchingToken: false,
    tokenFetchPromise: null as Promise<any> | null,
    // Store auth provider configuration
    authProviderType: null as string | null,
    msalConfigStored: null as any,
    // Configurable retry parameters
    retryConfig: {
        maxAttempts: 10,
        baseDelay: 1000,
        maxDelay: 30000,
        jitterPercent: 0.3
    },
    async install(app: typeof Vue, options: MicrosoftAuthOptions) {
        // Store configuration for later use
        this.authProviderType = options.authProviderType || 'MSAL';
        this.msalConfigStored = options.msalConfig;

        app.use(MsalPlugin, options.msalConfig);
        await this.setupAuthentication();
    },
    async errorHandler(this: any, err: AxiosError): Promise<any> {
        if (err?.response?.status != 401) return Promise.reject(err);

        try {
            await this.ensureAuthenticated();
            await this.fetchToken();
            // User info isn't needed by the retried request, so don't block on it.
            void userInfoService.fetchUserInfo({ forceFresh: true });

            const config = err.response.config;

            if (!config.headers) {
                config.headers = {};
            }
            config.headers.Authorization = axios.defaults.headers.Authorization;

            return axios(config);
        } catch (authError) {
            this.cachedToken = null;
            this.tokenExpiration = null;
            return Promise.reject(authError);
        }
    },
    async requestHandler(this: any, config: AxiosRequestConfig): Promise<any> {
        config.url = stripApiPrefix(config.url);

        await this.ensureAuthenticated();

        try {
            await userInfoService.fetchUserInfoIfNeeded(config.url);
        } catch (error) {
            // Don't fail the request - let it proceed and handle auth errors later.
            console.warn("Failed to fetch user info in request handler:", error);
        }

        if (!hasValidAuthHeader(config.headers?.Authorization)) {
            if (!this.isTokenValid()) {
                await this.fetchToken();
            }
            if (!config.headers) {
                config.headers = {};
            }
            config.headers.Authorization = axios.defaults.headers.Authorization;
        }

        return config;
    },
    async setupAuthentication(this: any): Promise<void> {
        axios.interceptors.response.use(
            response => response,
            (err: AxiosError) => this.errorHandler(err));
        axios.interceptors.request.use(
            (config) => this.requestHandler(config),
            (err) => this.errorHandler(err));
    },
    async fetchToken(forceRefresh: boolean = false): Promise<any> {
        if (!forceRefresh && this.isTokenValid()) {
            axios.defaults.headers.Authorization = `${BEARER_PREFIX}${this.cachedToken.accessToken}`;
            return this.cachedToken;
        }

        // Coalesce concurrent acquisitions onto a single in-flight promise.
        if (this.isFetchingToken && this.tokenFetchPromise) {
            return this.tokenFetchPromise;
        }

        this.isFetchingToken = true;
        this.tokenFetchPromise = this.performTokenAcquisition();

        try {
            return await this.tokenFetchPromise;
        } finally {
            this.isFetchingToken = false;
            this.tokenFetchPromise = null;
        }
    },
    async performTokenAcquisition(): Promise<any> {
        // Determine scopes based on auth provider type
        let scopes: string[];

        if (this.authProviderType === 'OIDC') {
            // Use scopes from OIDC config, fallback to openid
            scopes = this.msalConfigStored?.request?.scopes || ['openid'];
        } else {
            // Standard MSAL - use Azure AD API scope
            scopes = [`api://${Vue.prototype.$config.VITE_APP_ADAL_GRAPHID}/access_as_user`];
        }

        const token = await Vue.prototype.$msal.acquireToken(new ScopeSet(scopes));

        this.cachedToken = token;
        if (token.expiresOn) {
            this.tokenExpiration = new Date(token.expiresOn);
        } else {
            // Fallback: assume 1 hour expiration if MSAL doesn't provide one.
            this.tokenExpiration = new Date(Date.now() + 60 * 60 * 1000);
        }

        this.scheduleTokenRenewal();
        axios.defaults.headers.Authorization = `${BEARER_PREFIX}${token.accessToken}`;
        syncGroupClaims();

        return token;
    },
    async ensureAuthenticated() {
        if (Vue.prototype.$msal.isAuthenticated()) return;
        
        // If authentication is already in progress, return the existing promise
        if (this.isAuthenticating && this.authenticationPromise) {
            return this.authenticationPromise;
        }
        
        // Start authentication process
        this.isAuthenticating = true;
        this.authenticationPromise = this.performAuthentication();
        
        try {
            await this.authenticationPromise;
        } finally {
            // Clean up authentication state
            this.isAuthenticating = false;
            this.authenticationPromise = null;
        }
    },
    async performAuthentication() {
        const { maxAttempts } = this.retryConfig;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (!this.msalInteractionInProgress()) {
                if (Vue.prototype.$msal.isAuthenticated()) return;

                try {
                    await Vue.prototype.$msal.loginPopup();
                    // Group claims sync runs in performTokenAcquisition, once the
                    // freshly acquired token is set on the Authorization header.
                    return;
                } catch (error) {
                    console.warn(`Authentication attempt ${attempt + 1} failed:`, error);

                    if (attempt === maxAttempts - 1) {
                        throw new Error(`Authentication failed after ${maxAttempts} attempts: ${error}`);
                    }
                }
            }

            await new Promise(resolve => setTimeout(resolve, computeBackoffDelay(attempt, this.retryConfig)));
        }

        throw new Error("Authentication failed after multiple attempts");
    },
    msalInteractionInProgress(): boolean {
        const progress = !!(Vue.prototype.$msal.instance?.tokenCache?.storage?.getInteractionInProgress() ?? false);
        return progress;
    },
    isTokenValid(): boolean {
        if (!this.cachedToken || !this.tokenExpiration) {
            return false;
        }
        return Date.now() < this.tokenExpiration.getTime() - TOKEN_EXPIRY_BUFFER_MS;
    },
    scheduleTokenRenewal() {
        // Clear existing timer
        if (this.renewalTimer) {
            clearTimeout(this.renewalTimer);
            this.renewalTimer = null;
        }

        if (!this.tokenExpiration) return;

        // Schedule renewal 10 minutes before expiration (or halfway if token is short-lived)
        const now = new Date();
        const timeUntilExpiration = this.tokenExpiration.getTime() - now.getTime();
        const renewalBuffer = Math.min(10 * 60 * 1000, timeUntilExpiration / 2); // 10 minutes or half the token lifetime
        let renewalTime = Math.max(0, timeUntilExpiration - renewalBuffer);

        // Debug: override with fast interval so renewal can be observed (see DEBUG_RENEWAL_INTERVAL_MS).
        if (DEBUG_RENEWAL_INTERVAL_MS > 0) {
            renewalTime = DEBUG_RENEWAL_INTERVAL_MS;
            console.log(
                `[silent-renewal][DEBUG] next attempt in ${Math.round(renewalTime / 1000)}s ` +
                `(token expires ${this.tokenExpiration.toISOString()})`,
            );
        }

        if (renewalTime > 0) {
            this.renewalTimer = setTimeout(() => {
                this.silentTokenRenewal();
            }, renewalTime);
        }
    },
    async silentTokenRenewal() {
        try {
            let scopes: string[];
            if (this.authProviderType === 'OIDC') {
                scopes = this.msalConfigStored?.request?.scopes || ['openid'];
            } else {
                scopes = [`api://${Vue.prototype.$config.VITE_APP_ADAL_GRAPHID}/access_as_user`];
            }

            // Debug: log the renewal attempt (see DEBUG_RENEWAL_INTERVAL_MS).
            if (DEBUG_RENEWAL_INTERVAL_MS > 0) {
                console.log(`[silent-renewal][DEBUG] attempting acquireTokenSilent (scopes: ${scopes.join(", ")})`);
            }

            const token = await Vue.prototype.$msal.instance.acquireTokenSilent({
                scopes,
                account: Vue.prototype.$msal.user(),
                // Debug only: forces a real network refresh (bypasses MSAL cache) so the expiry
                // advances each tick. Off in committed code (see DEBUG_RENEWAL_INTERVAL_MS).
                ...(DEBUG_RENEWAL_INTERVAL_MS > 0 ? { forceRefresh: true } : {}),
            });

            this.cachedToken = token;
            if (token.expiresOn) {
                this.tokenExpiration = new Date(token.expiresOn);
            } else {
                this.tokenExpiration = new Date(Date.now() + 60 * 60 * 1000);
            }

            axios.defaults.headers.Authorization = `${BEARER_PREFIX}${token.accessToken}`;

            // Debug: log successful renewal and the new expiry (see DEBUG_RENEWAL_INTERVAL_MS).
            if (DEBUG_RENEWAL_INTERVAL_MS > 0) {
                console.log(
                    `[silent-renewal][DEBUG] SUCCESS — token renewed silently, ` +
                    `new expiry ${this.tokenExpiration.toISOString()}`,
                );
            }

            this.scheduleTokenRenewal();
            syncGroupClaims();
        } catch (error) {
            console.warn("Silent token renewal failed; will renew interactively on next request:", error);
            // Drop the cached token so the next request forces interactive renewal.
            this.cachedToken = null;
            this.tokenExpiration = null;
        }
    },
    /** Clears locally-cached auth/token state and the silent-renewal timer. Shared by both sign-out
     *  paths so popup and redirect logout tear down identically. */
    clearAuthState(this: any): void {
        if (this.renewalTimer) {
            clearTimeout(this.renewalTimer);
            this.renewalTimer = null;
        }
        this.cachedToken = null;
        this.tokenExpiration = null;
        this.isFetchingToken = false;
        this.tokenFetchPromise = null;
        this.isAuthenticating = false;
        this.authenticationPromise = null;
    },
    async logout(this: any): Promise<void> {
        this.clearAuthState();
        await Vue.prototype.$msal.logout();
    },
    /** Redirect-based sign-out used by the inactivity timeout: a full-page round trip to the AAD
     *  logout endpoint re-initializes the app on return (re-arming the inactivity timer) and avoids
     *  the per-tab popup that logoutPopup opens for every open tab. */
    async logoutRedirect(this: any, postLogoutRedirectUri?: string): Promise<void> {
        this.clearAuthState();
        await Vue.prototype.$msal.logoutRedirect(postLogoutRedirectUri);
    }
}
